import { useEffect, useState } from 'react';
import { uniq } from 'lodash';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Switch,
} from '@mui/material';
import CompanyUpdate from './CompanyUpdate';
import { status } from 'adminConstants/investorReporting';
import useInvestorReportingForm, {
  companyInformationSchema,
} from 'hooks/form/useInvestorReportingForm';
import { useInvestorReportingFormContext } from 'contexts/InvestorReportingFormContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDisclosure } from 'further-ui/hooks';
import { getCompanyLabel } from 'further-ui/utils';
import useStyles from './styles';
import useAccordionStyles from '../FundInformationStep/styles';
import ReportingDraftButtons from 'components/ReportingDraftButtons';
import PublishConfirmationDialog from './PublishConfirmationDialog';
import Heading from 'components/Heading';
import useExportReportingSample from 'hooks/data/investorReporting/useExportReportingSample';
import NotifyInvestorsTooltip from 'components/NotifyInvestorsTooltip';

const isCompanyUpdateProvidingData = (
  currentCompanyUpdate,
  allCompaniesUpdates,
) =>
  allCompaniesUpdates.some((companyUpdate) =>
    companyUpdate.copyFromCompanies.includes(currentCompanyUpdate.companyId),
  );

const getCompaniesWithSameLegalName = (
  allCompaniesData,
  currentCompanyUpdate,
) => {
  if (!isCompanyUpdateProvidingData(currentCompanyUpdate, allCompaniesData)) {
    const currentUpdateIndex = allCompaniesData.findIndex(
      ({ companyId }) => currentCompanyUpdate.companyId === companyId,
    );

    return allCompaniesData.filter(
      (companyData, index) =>
        currentUpdateIndex > index &&
        companyData.companyId !== currentCompanyUpdate.companyId &&
        companyData.legalName === currentCompanyUpdate.legalName &&
        !companyData.copyFromCompanies.length,
    );
  }
  return [];
};

const isCompanyUpdateCopyingFromAnother = (companyUpdateA, companyUpdateB) =>
  companyUpdateA.copyFromCompanies.includes(companyUpdateB.companyId);

const mergeCopiedCompany = (sourceCompanyUpdate, targetCompanyUpdate) => {
  const {
    additionalMetrics,
    media,
    mediaType,
    attachments,
    updateSummary,
    additionalTitle,
    additionalValue,
    description,
  } = sourceCompanyUpdate;

  return {
    ...targetCompanyUpdate,
    additionalMetrics,
    media,
    mediaType,
    attachments,
    updateSummary,
    additionalTitle,
    additionalValue,
    description,
    copyFromCompanies: [sourceCompanyUpdate.companyId],
  };
};

const CompanyInformationStep = ({
  handleStepBack,
  isViewPage,
  sortedCompanyUpdates,
}) => {
  const { classes } = useStyles();
  const { classes: accordionClasses, cx } = useAccordionStyles();

  const { submitForm, scrollToFirstError, isSubmitting } =
    useInvestorReportingForm();
  const formContext = useInvestorReportingFormContext();
  const {
    investorReport,
    firmId,
    companyInformation,
    setCompanyInformation,
    reportStatus,
    setNotifyInvestors,
    notifyInvestors,
  } = formContext;
  const investorReportId = investorReport?._id;
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [expandedCompanyIndices, setExpandedCompanyIndices] = useState([0]);
  const [mountedCompanyIndices, setMountedCompanyIndices] = useState([0]);
  const confirmationDisclosure = useDisclosure();

  const { exportSample, isPending } = useExportReportingSample();

  const { handleSubmit, control, setValue, getValues, formState, watch } =
    useForm({
      shouldFocusError: true,
      resolver: zodResolver(companyInformationSchema),
      criteriaMode: 'all',
      defaultValues: {
        companyInformation: sortedCompanyUpdates.map((update) => ({
          ...update,
          copiedFromAnotherCompany: update.copyFromCompanies.length > 0,
        })),
        notifyInvestors,
      },
    });

  useEffect(() => {
    const { unsubscribe } = watch(() => {
      setIsDirty(true);
    });

    return unsubscribe;
  }, [watch]);

  const handleCompanyUpdateChange = (companyUpdate, prevData, index) => {
    if (companyUpdate.copyFromCompanies?.length) {
      const companyCopyingFrom = prevData.find(
        ({ companyId }) => companyUpdate.copyFromCompanies[0] === companyId,
      );

      if (companyCopyingFrom) {
        prevData[index] = mergeCopiedCompany(companyCopyingFrom, companyUpdate);
      }
    } else {
      prevData[index] = companyUpdate;
    }

    const updatedCompaniesUpdates = prevData.map((targetCompanyUpdate) => {
      if (
        isCompanyUpdateCopyingFromAnother(targetCompanyUpdate, companyUpdate)
      ) {
        return mergeCopiedCompany(companyUpdate, targetCompanyUpdate);
      }
      return targetCompanyUpdate;
    });

    return updatedCompaniesUpdates;
  };

  const processQuickSave = async () => {
    const data = getValues();
    const updatedCompanies = sortedCompanyUpdates.reduce(
      (acc, company, index) =>
        handleCompanyUpdateChange(
          {
            ...company,
            ...data.companyInformation[index],
          },
          acc,
          index,
        ),
      companyInformation,
    );
    setCompanyInformation(updatedCompanies);

    await submitForm(
      { ...formContext, companyInformation: updatedCompanies },
      reportStatus ?? status.DRAFT,
      true,
    );
    setIsDirty(false);
  };

  const handleDownloadSample = () => {
    if (!isPending && investorReportId) {
      exportSample(investorReportId);
    }
  };

  const processManualFormSubmission = (selectedStatus) => async () => {
    const data = getValues();
    const updatedCompanies = sortedCompanyUpdates.reduce(
      (acc, company, index) =>
        handleCompanyUpdateChange(
          {
            ...company,
            ...data.companyInformation[index],
          },
          acc,
          index,
        ),
      companyInformation,
    );
    setCompanyInformation(updatedCompanies);

    setSubmitting(true);
    await submitForm(
      { ...formContext, companyInformation: updatedCompanies },
      selectedStatus,
      false,
    );
    setSubmitting(false);
    confirmationDisclosure.onClose();
  };

  const onValidationError = (errors) => {
    const errorIndices: Array<number> = [];
    errors?.companyInformation?.forEach((value, index) => {
      if (value !== undefined) {
        errorIndices.push(index);
      }
    });

    const mergedIndices = uniq([...expandedCompanyIndices, ...errorIndices]);
    setExpandedCompanyIndices(mergedIndices);
    setMountedCompanyIndices(mergedIndices);

    scrollToFirstError(errors);
  };

  const saveDraft = () => {
    const onSuccess = processManualFormSubmission(status.DRAFT);
    handleSubmit(onSuccess, onValidationError)();
  };

  const publishUpdate = () => {
    const onSuccess = confirmationDisclosure.onOpen;
    handleSubmit(onSuccess, onValidationError)();
  };

  const onConfirmPublishUpdate = () => {
    const onSuccess = processManualFormSubmission(status.SUBMITTED);
    handleSubmit(onSuccess)();
  };

  const handleAccordionToggle = (index) => {
    if (!expandedCompanyIndices.includes(index)) {
      setExpandedCompanyIndices([...expandedCompanyIndices, index]);
      setMountedCompanyIndices([...mountedCompanyIndices, index]);
    } else {
      setExpandedCompanyIndices(
        expandedCompanyIndices.filter((item) => item !== index),
      );
    }
  };

  const handleAnimationComplete = (index) => {
    if (!expandedCompanyIndices.includes(index)) {
      setMountedCompanyIndices(
        mountedCompanyIndices.filter((item) => item !== index),
      );
    }
  };

  return (
    <form>
      <Heading variant="h2">Enter company information</Heading>
      {!sortedCompanyUpdates?.length ? (
        <p style={{ marginBottom: '20px' }}>
          <strong>
            There were no firm holdings at the selected update date. To create
            an update regardless, please click <em>Publish update</em> below.
          </strong>
        </p>
      ) : (
        sortedCompanyUpdates.map((single, index) => (
          <Accordion
            key={single.companyId}
            className={accordionClasses.customAccordion}
            expanded={expandedCompanyIndices.includes(index)}
            slotProps={{
              transition: () => ({
                onExited: () => handleAnimationComplete(index),
              }),
            }}
          >
            <AccordionSummary
              className={cx(
                accordionClasses.cmAccHeader,
                accordionClasses.expends,
              )}
              aria-controls="panel1bh-content"
              onClick={() => handleAccordionToggle(index)}
            >
              <div className={accordionClasses.accordionHeader}>
                <span className={accordionClasses.accordionHeaderFundName}>
                  {getCompanyLabel(single)}
                </span>
              </div>
            </AccordionSummary>
            <AccordionDetails className={accordionClasses.cmAccDetail}>
              {mountedCompanyIndices.includes(index) && (
                <CompanyUpdate
                  index={index}
                  single={single}
                  duplicateEntries={getCompaniesWithSameLegalName(
                    companyInformation,
                    single,
                  )}
                  investorReportId={investorReportId}
                  firmId={firmId}
                  control={control}
                  setValue={setValue}
                  isViewPage={isViewPage}
                  enableLoadLastUpdate={reportStatus !== status.SUBMITTED}
                />
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}
      <div className={classes.buttonRow}>
        <div className={classes.buttonLeftContainer}>
          <Button
            variant="outlined"
            onClick={handleStepBack}
            data-testid={`nav-back`}
          >
            Back
          </Button>
          {reportStatus !== status.SUBMITTED && !isViewPage && (
            <Button
              variant="outlined"
              data-testid={`submit-draft`}
              disabled={submitting}
              type="button"
              name="draft"
              onClick={saveDraft}
            >
              Save draft and exit
            </Button>
          )}
        </div>

        <div className={classes.buttonRowMutations}>
          {!isViewPage && (
            <>
              <NotifyInvestorsTooltip title="If toggled, investors will receive an email notifying them of this report’s publication.">
                <Switch
                  checked={!!notifyInvestors}
                  onChange={(_, checked) => {
                    setValue('notifyInvestors', !!checked);
                    setNotifyInvestors(!!checked);
                  }}
                />
              </NotifyInvestorsTooltip>
              <Button
                variant="contained"
                color="primary"
                disabled={submitting}
                type="button"
                name="publish"
                onClick={publishUpdate}
                data-testid={`submit`}
              >
                {investorReportId ? 'Publish update' : 'Submit'}
              </Button>
            </>
          )}
        </div>
      </div>
      <ReportingDraftButtons
        onQuickSave={processQuickSave}
        isQuickSaving={isSubmitting}
        onDownloadSample={handleDownloadSample}
        isDownloading={isPending}
        allowDownload={!!investorReportId && !isDirty}
        showDownloadButton={true}
        disabled={reportStatus === status.SUBMITTED || isViewPage}
      />
      {Object.keys(formState.errors).length > 0 && (
        <div className={classes.overallFormError}>
          There are one or more errors in the company information above. Please
          correct all errors and try again.
        </div>
      )}
      <PublishConfirmationDialog
        disclosure={confirmationDisclosure}
        reportStatus={reportStatus}
        onConfirmPublishUpdate={onConfirmPublishUpdate}
        submitting={submitting}
        notifyInvestors={notifyInvestors}
      />
    </form>
  );
};

export default CompanyInformationStep;
